// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { FetchBaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/query';

/**
 * Result of a completed polling run: either success with data or failure with
 * error.
 */
export type PollResult<T, E> =
  | { data: T; error?: undefined }
  | { data?: undefined; error: E };

/** Options for {@link pollWithBackoff}. */
export interface PollWithBackoffOptions<T, E = FetchBaseQueryError> {
  /**
   * Performs one poll. Typically returns the result of `fetchWithBQ(...)` or
   * `dispatch(someEndpoint.initiate())` so it conforms to QueryReturnValue.
   */
  poll: () => Promise<QueryReturnValue<T, E>>;

  /**
   * Return `true` to schedule the next poll after the backoff delay, `false` to
   * stop and resolve with `getResult(lastResponse)`.
   */
  shouldContinue: (response: QueryReturnValue<T, E>) => boolean;

  /**
   * Called when `shouldContinue` has just returned `false`. Maps the last response
   * to the final success or error result.
   */
  getResult: (response: QueryReturnValue<T, E>) => PollResult<T, E>;

  /** Base interval in ms between polls. Default `5000`. */
  pollInterval?: number;

  /**
   * Maximum delay in ms for a single wait. When the next scheduled wait would
   * exceed this, polling stops and `onTimeout()` is used as the error. Default
   * `900_000` (15 min).
   */
  maxWait?: number;

  /**
   * Backoff strategy:
   *
   * - `'linear'`: wait = pollInterval * attempt (1st wait = pollInterval, 2nd =
   *   2*pollInterval, …)
   * - `'constant'`: wait = pollInterval every time. Default `'linear'`.
   */
  backoff?: 'constant' | 'linear';

  /**
   * Called when the next wait would exceed `maxWait`. Return value is used as `{
   * error }`. If omitted, a generic timeout error is used.
   */
  onTimeout?: () => E;
}

const defaultTimeoutError = {
  status: 'TIMEOUT_ERROR',
  error: 'POLLING_TIMED_OUT',
} as FetchBaseQueryError;

/**
 * Polls an async operation (e.g. RTK Query / fetch) with a configurable interval
 * and backoff until a terminal state or timeout.
 *
 * Stops immediately on polling error (`response.error`). Otherwise uses
 * `shouldContinue` to decide whether to keep polling; when it returns `false`,
 * `getResult(lastResponse)` gives the final `{ data }` or `{ error }`. If the next
 * wait would exceed `maxWait`, polling stops and the timeout error is returned.
 *
 * @example
 *
 * ```ts
 * const result = await pollWithBackoff({
 *   poll: () =>
 *     fetchWithBQ({
 *       url: `/v2/aggregator/callback/${taskId}/`,
 *       method: 'GET',
 *     }),
 *   shouldContinue: res => {
 *     if (res.error) return false;
 *     const s = res.data?.in_progress?.status;
 *     return s !== 'Processed' && s !== 'Error';
 *   },
 *   getResult: res => {
 *     if (res.error) return { error: res.error };
 *     if (res.data.in_progress.status === 'Error') {
 *       return {
 *         error: {
 *           status: 'FETCH_ERROR',
 *           error: res.data.in_progress.message,
 *         },
 *       };
 *     }
 *     return { data: res.data };
 *   },
 *   pollInterval: 5000,
 *   maxWait: 900_000,
 *   backoff: 'linear',
 * });
 * ```
 *
 * @param options - See {@link PollWithBackoffOptions}
 * @returns Promise that resolves to `{ data }` or `{ error }`
 */
export async function pollWithBackoff<T, E = FetchBaseQueryError>(
  options: PollWithBackoffOptions<T, E>,
): Promise<PollResult<T, E>> {
  const {
    poll,
    shouldContinue,
    getResult,
    pollInterval = 5000,
    maxWait = 900_000,
    backoff = 'linear',
    onTimeout,
  } = options;

  let attempt = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await poll();

    if (response.error) {
      return { error: response.error };
    }

    if (!shouldContinue(response)) {
      return getResult(response);
    }

    attempt += 1;
    const waitMs = backoff === 'linear' ? pollInterval * attempt : pollInterval;

    if (waitMs > maxWait) {
      return {
        error: (onTimeout?.() ?? defaultTimeoutError) as E,
      };
    }

    await new Promise<void>(resolve => {
      setTimeout(resolve, waitMs);
    });
  }
}
